import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormField,
  Header,
  Input,
  Modal,
  Multiselect,
  MultiselectProps,
  RadioGroup,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import {
  useInvestigationList,
  useMergeInvestigations,
} from "../../hooks/useInvestigations";
import { InvestigationRule } from "./InvestigationRule";
import {
  STATUS_LABELS,
  caseRules,
  isTerminal,
  ownerLabel,
  relatedOpenCases,
} from "./investigationUtils";

/** Cases per merge request (mirrors the backend cap); longer selections are chunked. */
export const MAX_MERGE_SOURCES = 10;

/** Statuses a case must not be in to take part in a merge (mirrors the backend). */
export const mergeBlocker = (inv: Investigation): string | null => {
  if (isTerminal(inv.status))
    return `${STATUS_LABELS[inv.status] ?? inv.status}`;
  if (inv.status === "EXECUTING_REDRIVE" || inv.status === "MONITORING_REDRIVE")
    return "redrive in progress";
  if (inv.pendingDeletion) return "deletion awaiting MCM approval";
  if (inv.pendingLaunch) return "an attempt is being prepared";
  if (inv.mergePending) return "a merge is still moving its items";
  return null;
};

interface InvestigationMergeDialogProps {
  stage: Stage;
  /**
   * Cases already chosen (from the registry's multi-select, or the page's own case). The dialog
   * lets the user pick the survivor among them and add further sources.
   */
  candidates: Investigation[];
  /** Pre-selected survivor (the investigation page's own case). */
  defaultSurvivorId?: string;
  onDismiss: () => void;
  /** Called with the survivor id after a successful merge. */
  onMerged: (survivorId: string, mergedIds: string[], pending: boolean) => void;
}

const optionFor = (inv: Investigation): MultiselectProps.Option => ({
  value: inv.id,
  label: inv.title,
  description: `${ownerLabel(inv.owner)} · ${
    STATUS_LABELS[inv.status] ?? inv.status
  } · ${(inv.assignedCount ?? 0).toLocaleString()} items`,
  disabled: !!mergeBlocker(inv),
  ...(mergeBlocker(inv) ? { labelTag: mergeBlocker(inv) ?? undefined } : {}),
});

/**
 * Merge dialog: choose the survivor, confirm the sources, optionally rename. Anyone may merge.
 * The copy states what happens to the sources (closed, items and rule move, cannot be
 * reopened) and names both owners, because this is the first action that closes someone
 * else's case without their involvement.
 */
export const InvestigationMergeDialog: React.FC<
  InvestigationMergeDialogProps
> = ({ stage, candidates, defaultSurvivorId, onDismiss, onMerged }) => {
  const active = useInvestigationList(stage, { view: "active" });
  const merge = useMergeInvestigations(stage);
  const [survivorId, setSurvivorId] = useState<string>(
    defaultSurvivorId ?? candidates[0]?.id ?? ""
  );
  const [extraIds, setExtraIds] = useState<string[]>([]);
  // Prefilled with the survivor's current title so it can be edited rather than retyped;
  // follows the survivor choice until the user types their own.
  const [title, setTitle] = useState(
    () =>
      candidates.find((c) => c.id === (defaultSurvivorId ?? candidates[0]?.id))
        ?.title ?? ""
  );
  const [titleEdited, setTitleEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const byId = useMemo(() => {
    const map = new Map<string, Investigation>();
    [...candidates, ...(active.items ?? [])].forEach((inv) =>
      map.set(inv.id, inv)
    );
    return map;
  }, [candidates, active.items]);
  const survivor = byId.get(survivorId);
  const sourceIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...candidates.map((c) => c.id).filter((id) => id !== survivorId),
          ...extraIds,
        ])
      ),
    [candidates, extraIds, survivorId]
  );
  const sources = sourceIds
    .map((id) => byId.get(id))
    .filter((inv): inv is Investigation => !!inv);
  const blocked = [
    ...(survivor && mergeBlocker(survivor)
      ? [{ inv: survivor, why: mergeBlocker(survivor) as string }]
      : []),
    ...sources
      .map((inv) => ({ inv, why: mergeBlocker(inv) }))
      .filter((b): b is { inv: Investigation; why: string } => !!b.why),
  ];

  // Suggest same-category open cases first: those are the ones most likely to be the same bug.
  const suggestions = useMemo(() => {
    if (!survivor) return [];
    const related = new Set(
      caseRules(survivor)
        .flatMap((branch) =>
          relatedOpenCases(branch, active.items ?? [], survivor.id)
        )
        .map((r) => r.investigation.id)
    );
    return (active.items ?? [])
      .filter(
        (inv) =>
          inv.id !== survivorId && !candidates.some((c) => c.id === inv.id)
      )
      .sort((a, b) => Number(related.has(b.id)) - Number(related.has(a.id)))
      .map((inv) => ({
        ...optionFor(inv),
        ...(related.has(inv.id) ? { tags: ["same category"] } : {}),
      }));
  }, [active.items, survivor, survivorId, candidates]);

  const totalItems =
    (survivor?.population?.count ?? 0) +
    sources.reduce((n, s) => n + (s.population?.count ?? 0), 0);
  const canMerge = !!survivor && sources.length > 0 && blocked.length === 0;

  const submit = async () => {
    if (!survivor) return;
    setError(null);
    const merged: string[] = [];
    let pending = false;
    try {
      // The backend caps sources per call; a version bump follows every call, so re-read it.
      let { version } = survivor;
      for (let i = 0; i < sourceIds.length; i += MAX_MERGE_SOURCES) {
        const chunk = sourceIds.slice(i, i + MAX_MERGE_SOURCES);
        // Sequential on purpose: every call bumps the survivor's version.
        // eslint-disable-next-line no-await-in-loop
        const result = await merge.mutateAsync({
          investigationId: survivor.id,
          request: {
            expectedVersion: version,
            sourceInvestigationIds: chunk,
            ...(i === 0 && title.trim() && title.trim() !== survivor.title
              ? { title: title.trim() }
              : {}),
          },
        });
        version = result.investigation.version;
        merged.push(...(result.mergedInvestigationIds ?? []));
        pending = pending || !!result.mergePending;
      }
      onMerged(survivor.id, merged, pending);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Merge failed");
    }
  };

  return (
    <Modal
      visible
      size="large"
      onDismiss={onDismiss}
      header={
        <Header
          variant="h2"
          description="The survivor keeps its id, owner, status and comments; its rule gains the other cases' rules as OR branches and its population becomes the union. The other cases are closed as Merged and cannot be reopened — split the survivor if part of it needs its own case again."
        >
          Merge investigations
        </Header>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!canMerge}
              loading={merge.isLoading}
              onClick={() => {
                submit().catch(console.error);
              }}
            >
              Merge {sources.length || ""} into survivor
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {candidates.length > 1 && (
          <FormField
            label="Survivor"
            description="The case that stays open and absorbs the others."
          >
            <RadioGroup
              value={survivorId}
              onChange={({ detail }) => {
                setSurvivorId(detail.value);
                if (!titleEdited)
                  setTitle(
                    candidates.find((c) => c.id === detail.value)?.title ?? ""
                  );
              }}
              items={candidates.map((inv) => ({
                value: inv.id,
                label: inv.title,
                description: `${ownerLabel(inv.owner)} · ${
                  STATUS_LABELS[inv.status] ?? inv.status
                } · ${(inv.population?.count ?? 0).toLocaleString()} items`,
              }))}
            />
          </FormField>
        )}
        {survivor && (
          <div>
            <Box variant="awsui-key-label">Survivor rule</Box>
            <InvestigationRule rules={caseRules(survivor)} compact />
          </div>
        )}
        <FormField
          label="Investigations to merge into it"
          description="Same-category open cases are listed first. Resolved cases, cases mid-redrive and cases awaiting an MCM cannot be merged."
        >
          <Multiselect
            placeholder="Add investigations…"
            filteringType="auto"
            statusType={active.isLoading ? "loading" : "finished"}
            loadingText="Loading active investigations"
            options={suggestions}
            selectedOptions={extraIds
              .map((id) => byId.get(id))
              .filter((inv): inv is Investigation => !!inv)
              .map(optionFor)}
            onChange={({ detail }) =>
              setExtraIds(
                detail.selectedOptions
                  .map((o) => o.value)
                  .filter((v): v is string => !!v)
              )
            }
          />
        </FormField>
        {sources.length > 0 && (
          <SpaceBetween size="xs">
            {sources.map((inv) => (
              <div key={inv.id} data-testid="merge-source">
                <SpaceBetween
                  direction="horizontal"
                  size="xs"
                  alignItems="center"
                >
                  <strong>{inv.title}</strong>
                  <Box color="text-body-secondary" fontSize="body-s">
                    {ownerLabel(inv.owner)} ·{" "}
                    {(inv.population?.count ?? 0).toLocaleString()} items
                  </Box>
                  {mergeBlocker(inv) && (
                    <StatusIndicator type="error">
                      {mergeBlocker(inv)}
                    </StatusIndicator>
                  )}
                </SpaceBetween>
                <InvestigationRule rules={caseRules(inv)} compact />
              </div>
            ))}
          </SpaceBetween>
        )}
        <FormField
          label="New title for the survivor"
          description="Optional. The root cause is usually understood by the time cases are merged; name it."
        >
          <Input
            value={title}
            onChange={({ detail }) => {
              setTitle(detail.value);
              setTitleEdited(true);
            }}
          />
        </FormField>
        {survivor && sources.length > 0 && (
          <Alert type="warning" header="This closes the merged investigations">
            {sources
              .map((s) => `“${s.title}” (${ownerLabel(s.owner)})`)
              .join(", ")}{" "}
            cannot be reopened. Their{" "}
            {sources
              .reduce((n, s) => n + (s.population?.count ?? 0), 0)
              .toLocaleString()}{" "}
            items move to “{survivor.title}” ({totalItems.toLocaleString()}{" "}
            total).
          </Alert>
        )}
        {blocked.length > 0 && (
          <Alert type="error" header="Some cases cannot be merged right now">
            <ul>
              {blocked.map((b) => (
                <li key={b.inv.id}>
                  {b.inv.title}: {b.why}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
};

interface InvestigationMergeActionProps {
  stage: Stage;
  investigation: Investigation;
  /** Called after the merge with the survivor id (this investigation) so the page can refresh. */
  onMerged?: (mergedIds: string[], pending: boolean) => void;
  /** Controlled mode (opened from a menu): no button; the dialog shows while `open` is true. */
  open?: boolean;
  onClose?: () => void;
}

/**
 * "Merge into…" header action on an investigation page: this case is the survivor; the dialog
 * picks the cases to absorb. Anyone may merge. Hidden once the case is closed.
 */
export const InvestigationMergeAction: React.FC<
  InvestigationMergeActionProps
> = ({ stage, investigation, onMerged, open: controlledOpen, onClose }) => {
  const [ownOpen, setOwnOpen] = useState(false);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : ownOpen;
  const setOpen = (next: boolean) => {
    if (controlled) {
      if (!next) onClose?.();
    } else setOwnOpen(next);
  };
  if (isTerminal(investigation.status)) return null;
  const blocker = mergeBlocker(investigation);
  return (
    <>
      {!controlled && (
        <Button
          iconName="shrink"
          disabled={!!blocker}
          disabledReason={blocker ? `Cannot merge: ${blocker}.` : undefined}
          onClick={() => setOpen(true)}
        >
          Merge
        </Button>
      )}
      {open && (
        <InvestigationMergeDialog
          stage={stage}
          candidates={[investigation]}
          defaultSurvivorId={investigation.id}
          onDismiss={() => setOpen(false)}
          onMerged={(_survivor, mergedIds, pending) => {
            setOpen(false);
            onMerged?.(mergedIds, pending);
          }}
        />
      )}
    </>
  );
};

export default InvestigationMergeAction;
